import { Form, Input, Select, type FormInstance } from "antd";
import { useCallback, useEffect, useState } from "react";
import type { FieldConfig, SourcingNodeConfig } from "../../../type";
import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import SelectSupplierModal from "@/components/supplier/SelectSupplierModal";
import type { SupplierUserSimpleListItem } from "@/types/apis";
import type { SupplierStageCode } from "@/constants";

// 供应商选择状态管理
// 负责管理供应商选择弹窗的开关状态、当前操作的字段、以及已选供应商的详情映射
function useSupplierSelection(form: FormInstance) {
  // 弹窗开关状态
  const [modalOpen, setModalOpen] = useState(false);
  // 当前打开弹窗对应的字段名（用于知道往哪个字段添加供应商）
  const [currentField, setCurrentField] = useState<string>("");
  // 当前供应商阶段筛选条件
  const [currentSupplierStage, setCurrentSupplierStage] = useState<
    SupplierStageCode | undefined
  >();
  // 供应商详情映射 {字段名: 供应商详情数组}，用于回显供应商名称
  const [suppliersMap, setSuppliersMap] = useState<
    Record<string, SupplierUserSimpleListItem[]>
  >({});

  // 关闭弹窗
  const closeModal = useCallback(() => setModalOpen(false), []);

  // 打开供应商选择弹窗，记录当前操作的字段和阶段筛选
  const openModal = useCallback(
    (fieldName: string, supplierStage?: SupplierStageCode) => {
      setCurrentField(fieldName);
      setCurrentSupplierStage(supplierStage);
      setModalOpen(true);
    },
    [],
  );

  // 初始化供应商列表（用于数据回显）
  // 将后端返回的 supplierList 转换为完整格式，并同步到表单值和 suppliersMap
  const initSuppliersMap = useCallback(
    (
      supplierList: NonNullable<SourcingNodeConfig["extra"]>["supplierList"],
      fieldName: string,
    ) => {
      if (!supplierList || supplierList.length === 0) return;

      const suppliers: SupplierUserSimpleListItem[] = supplierList.map((s) => ({
        id: s.id,
        supplierName: s.supplierName,
        supplierUsName: s.supplierUsName,
        supplierCode: "",
        supplierStage: 0,
      }));

      setSuppliersMap((prev) => ({
        ...prev,
        [fieldName]: suppliers,
      }));

      form.setFieldValue(
        fieldName,
        supplierList.map((s) => s.id),
      );
    },
    [form],
  );

  // 添加选中的供应商（去重后添加到表单和映射中）
  const addSuppliers = useCallback(
    (newSuppliers: SupplierUserSimpleListItem[]) => {
      if (!currentField) return;

      const existingIds = form.getFieldValue(currentField) || [];
      const suppliersToAdd = newSuppliers.filter(
        (supplier) => !existingIds.includes(supplier.id),
      );
      if (suppliersToAdd.length === 0) return;

      form.setFieldValue(currentField, [
        ...existingIds,
        ...suppliersToAdd.map((s) => s.id),
      ]);

      setSuppliersMap((prev) => ({
        ...prev,
        [currentField]: [...(prev[currentField] || []), ...suppliersToAdd],
      }));
    },
    [currentField, form],
  );

  // 移除指定供应商（从表单值和映射中删除）
  const removeSupplier = useCallback(
    (fieldName: string, supplierId: number) => {
      const ids = form.getFieldValue(fieldName) || [];
      form.setFieldValue(
        fieldName,
        ids.filter((id: number | string) => Number(id) !== supplierId),
      );

      setSuppliersMap((prev) => ({
        ...prev,
        [fieldName]:
          prev[fieldName]?.filter((s) => Number(s.id) !== supplierId) || [],
      }));
    },
    [form],
  );

  // 获取指定字段的供应商详情列表
  const getSuppliers = useCallback(
    (fieldName: string) => suppliersMap[fieldName] || [],
    [suppliersMap],
  );

  return {
    modalOpen,
    openModal,
    addSuppliers,
    removeSupplier,
    getSuppliers,
    initSuppliersMap,
    currentSupplierStage,
    closeModal,
  };
}

// 供应商选择字段组件
// 只读 Select，点击打开供应商选择弹窗，支持取消选择
const SupplierSelectField: React.FC<{
  field: { name: string; label: string; required?: boolean };
  selectedSuppliers: SupplierUserSimpleListItem[];
  onOpenModal: () => void;
  onRemove: (id: number) => void;
  disabled: boolean;
}> = ({ field, selectedSuppliers, onOpenModal, onRemove, disabled }) => (
  <Form.Item
    name={field.name}
    label={field.label}
    rules={field.required ? [{ required: true }] : []}
  >
    <Select
      mode="multiple"
      open={false}
      onClick={disabled ? void 0 : onOpenModal}
      onDeselect={disabled ? void 0 : (value) => onRemove(Number(value))}
      placeholder="点击选择供应商"
      options={selectedSuppliers.map((s) => ({
        value: s.id,
        label: s.supplierName,
      }))}
    />
  </Form.Item>
);

// 表单项渲染器
// 根据字段类型渲染对应的表单项组件
const FieldRenderer: React.FC<{
  field: FieldConfig;
  selectedSuppliers?: SupplierUserSimpleListItem[];
  onOpenSupplierModal: () => void;
  onRemoveSupplier?: (id: number) => void;
  disabled: boolean;
}> = (props) => {
  const { field, disabled } = props;

  switch (field.type) {
    case "input":
      return (
        <Form.Item
          name={field.name}
          label={field.label}
          rules={field.required ? [{ required: true }] : []}
        >
          <Input placeholder={field.placeholder} />
        </Form.Item>
      );

    case "upload":
      return (
        <MaterialFileFormFields
          required={field.required}
          label={field.label}
          fieldName={field.name}
          hiddenRemove={disabled}
        />
      );

    case "supplierSelect":
      return (
        <SupplierSelectField
          field={field}
          selectedSuppliers={props.selectedSuppliers || []}
          onOpenModal={props.onOpenSupplierModal}
          onRemove={props.onRemoveSupplier || (() => {})}
          disabled={disabled}
        />
      );
  }
};

// 寻源节点表单组件
// 负责渲染节点配置的字段列表，管理供应商选择弹窗
export const NodeForm: React.FC<{
  disabled: boolean;
  node: SourcingNodeConfig;
  form: FormInstance;
}> = ({ node, form, disabled }) => {
  const {
    modalOpen,
    openModal,
    addSuppliers,
    removeSupplier,
    getSuppliers,
    initSuppliersMap,
    currentSupplierStage,
    closeModal,
  } = useSupplierSelection(form);

  // 初始化供应商列表（用于回显）
  // 当 node.extra.supplierList 存在时，为所有 supplierSelect 类型字段初始化供应商数据
  useEffect(() => {
    if (!node.extra?.supplierList) return;
    node.fields
      .filter(
        (f): f is Extract<FieldConfig, { type: "supplierSelect" }> =>
          f.type === "supplierSelect",
      )
      .forEach((field) => {
        initSuppliersMap(node.extra!.supplierList!, field.name);
      });
  }, [node.extra, node.fields, initSuppliersMap]);

  return (
    <>
      {/* 使用 component={false} 避免渲染真实 <form> 标签，防止嵌套警告 */}
      <Form form={form} component={false} disabled={disabled}>
        {node.fields.map((f) => (
          <FieldRenderer
            key={f.name}
            field={f}
            disabled={disabled}
            selectedSuppliers={getSuppliers(f.name)}
            onOpenSupplierModal={() =>
              openModal(f.name, "supplierStage" in f ? f.supplierStage : void 0)
            }
            onRemoveSupplier={(id) => removeSupplier(f.name, id)}
          />
        ))}
      </Form>

      <SelectSupplierModal
        open={modalOpen}
        multiple
        fixedSupplierStage={currentSupplierStage}
        onOkMulti={(suppliers) => {
          if (suppliers) addSuppliers(suppliers);
          closeModal();
        }}
      />
    </>
  );
};
